import { useEffect, useState } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { authFetch } from "@/lib/authFetch";
import { Loader2, X } from "lucide-react";

const CONTRACT_LABELS: Record<string, string> = {
  revendeur: "Revendeur",
  revendeur_premium: "Revendeur Premium",
  sous_distributeur: "Sous-distributeur",
  apporteur_affaires: "Apporteur d'affaires",
  annule: "Annulé",
};

function colorForCount(c: number): string {
  if (!c) return "#52525b";
  if (c === 1) return "#22c55e";
  if (c === 2) return "#f59e0b";
  return "#ef4444";
}

interface CoverageReseller {
  id: string;
  companyName: string;
  logoUrl?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contractStatus: string;
  actif: boolean;
}

interface CoverageEntry {
  count: number;
  revendeurs: CoverageReseller[];
}

interface SelectedDept {
  code: string;
  nom: string;
  count: number;
  revendeurs: CoverageReseller[];
}

interface Props {
  onOpenReseller?: (id: string) => void;
}

export default function ResellersCoverage({ onOpenReseller }: Props) {
  const [geo, setGeo] = useState<any>(null);
  const [coverage, setCoverage] = useState<Record<string, CoverageEntry>>({});
  const [selected, setSelected] = useState<SelectedDept | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch("/data/departements.geojson").then((r) => r.json()),
      authFetch("/api/resellers/zones/coverage").then((r) => r.json()),
    ]).then(([geoData, coverageData]) => {
      setGeo(geoData);
      setCoverage(coverageData.coverage || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const geoKey =
    Object.keys(coverage).length +
    ":" +
    Object.values(coverage).reduce((a, b) => a + b.count, 0);

  const style = (feature: any) => {
    const c = coverage[feature.properties.code]?.count || 0;
    return {
      fillColor: colorForCount(c),
      weight: 1,
      color: "#1f2937",
      fillOpacity: 0.55,
    };
  };

  const onEachFeature = (feature: any, layer: any) => {
    const code = feature.properties.code;
    const entry = coverage[code];
    const count = entry?.count || 0;
    layer.bindTooltip(
      `<strong>${feature.properties.nom}</strong> (${code})<br/>${count} revendeur${count > 1 ? "s" : ""}`,
      { sticky: true }
    );
    layer.on("click", () =>
      setSelected({
        code,
        nom: feature.properties.nom,
        count,
        revendeurs: entry?.revendeurs || [],
      })
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
        <Loader2 className="w-5 h-5 animate-spin" />
        Chargement de la carte de couverture…
      </div>
    );
  }

  const displayName = (r: CoverageReseller) => r.companyName;
  const contactName = (r: CoverageReseller) =>
    [r.contactFirstName, r.contactLastName].filter(Boolean).join(" ") || null;

  return (
    <div style={{ position: "relative" }}>
      <div style={{ height: "72vh", width: "100%", borderRadius: 12, overflow: "hidden" }}>
        <MapContainer
          center={[46.6, 2.5]}
          zoom={6}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {geo && (
            <GeoJSON
              key={geoKey}
              data={geo}
              style={style}
              onEachFeature={onEachFeature}
            />
          )}
        </MapContainer>
      </div>

      {/* Légende */}
      <div
        style={{
          position: "absolute",
          bottom: 16,
          left: 16,
          zIndex: 1000,
          background: "var(--background, #fff)",
          color: "var(--foreground, #111)",
          borderRadius: 8,
          padding: "8px 12px",
          fontSize: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,.2)",
          border: "1px solid rgba(0,0,0,.1)",
          lineHeight: 1.8,
        }}
      >
        <div><span style={{ display: "inline-block", width: 12, height: 12, background: "#52525b", borderRadius: 2, marginRight: 6, verticalAlign: "middle" }} />0 — opportunité</div>
        <div><span style={{ display: "inline-block", width: 12, height: 12, background: "#22c55e", borderRadius: 2, marginRight: 6, verticalAlign: "middle" }} />1 — couvert</div>
        <div><span style={{ display: "inline-block", width: 12, height: 12, background: "#f59e0b", borderRadius: 2, marginRight: 6, verticalAlign: "middle" }} />2 — concurrence</div>
        <div><span style={{ display: "inline-block", width: 12, height: 12, background: "#ef4444", borderRadius: 2, marginRight: 6, verticalAlign: "middle" }} />3+ — forte concurrence</div>
      </div>

      {/* Panneau latéral */}
      {selected && (
        <div
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1000,
            width: 300,
            maxHeight: "68vh",
            overflowY: "auto",
            background: "var(--background, #fff)",
            color: "var(--foreground, #111)",
            borderRadius: 10,
            padding: 14,
            boxShadow: "0 4px 16px rgba(0,0,0,.25)",
            border: "1px solid rgba(0,0,0,.08)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <strong style={{ fontSize: 14 }}>
              {selected.nom} <span style={{ color: "#6b7280" }}>({selected.code})</span>
            </strong>
            <button
              onClick={() => setSelected(null)}
              style={{ border: 0, background: "none", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 2px", color: "#6b7280" }}
              aria-label="Fermer"
            >
              <X size={16} />
            </button>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
            {selected.count} revendeur{selected.count > 1 ? "s" : ""}
          </div>

          {selected.count === 0 ? (
            <div style={{ color: "#16a34a", fontSize: 13 }}>
              Zone blanche — opportunité commerciale.
            </div>
          ) : (
            selected.revendeurs.map((r) => (
              <div
                key={r.id}
                style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderTop: "1px solid rgba(0,0,0,.06)" }}
              >
                {r.logoUrl ? (
                  <img
                    src={r.logoUrl}
                    alt=""
                    style={{ width: 32, height: 32, borderRadius: 6, objectFit: "contain", background: "#f9fafb", flexShrink: 0, border: "1px solid rgba(0,0,0,.06)" }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div
                    style={{ width: 32, height: 32, borderRadius: 6, background: "#2563eb", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 }}
                  >
                    {(displayName(r) || "?")[0].toUpperCase()}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {displayName(r)}
                  </div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>
                    {contactName(r)}
                    {!r.actif && " · inactif"}
                    {r.contractStatus && (
                      <span> · {CONTRACT_LABELS[r.contractStatus] ?? r.contractStatus}</span>
                    )}
                  </div>
                  {onOpenReseller && (
                    <button
                      type="button"
                      onClick={() => onOpenReseller(r.id)}
                      style={{ background: "none", border: 0, color: "#2563eb", cursor: "pointer", padding: 0, font: "inherit", textDecoration: "underline", fontSize: 12 }}
                    >
                      Ouvrir la fiche →
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
